/**
 * React Query cache keys and configuration for Meetball
 */
// AIDEV-TODO: Mirror the @lib/cache/questions and @lib/cache/spaces factory
// pattern for chat domain — current consumers (useChat, useUnreadCount,
// useRealtimeInvalidation chat handlers) still mix CACHE_KEYS access with
// raw `['messages', roomId]` / `['notifications']` literals.
export const CACHE_KEYS = {
  /** Base key for room-based message queries. Must stay in sync with roomMessages(). */
  roomMessagesBase: ['messages'] as const,
  /** Base key for user-based (DM) message queries. Must stay in sync with userMessages(). */
  userMessagesBase: ['user-messages'] as const,

  // Chat list for current user
  chatList: ['chats'] as const,

  // Individual chat room data
  chatRoom: (roomId: string) => ['chats', 'rooms', roomId] as const,

  // Messages for specific room
  chatMessages: (roomId: string) => ['chats', 'rooms', roomId, 'messages'] as const,

  // Room metadata (participants, question context)
  chatMetadata: (roomId: string) => ['chats', 'rooms', roomId, 'metadata'] as const,

  // User's room list
  userRooms: (userId: string) => ['chats', 'users', userId, 'rooms'] as const,

  // Offline message queue
  offlineQueue: ['chats', 'offline', 'queue'] as const,

  // Block user management
  blockedUsers: ['blocked-users'] as const,
  blockCheck: (userId: string) => ['block-check', userId] as const,

  // Room-based messages (anonymous virtual rooms)
  roomMessages: (roomId: string, questionId?: string | null) =>
    ['messages', roomId, questionId ?? null] as const,

  // Room-message prefix (all question-scoped variants for a room). React Query
  // invalidates by prefix, so this 2-element key matches every roomMessages()
  // entry for the room. Use it for room-wide fallback invalidation — never
  // roomMessages(roomId), which is the 3-element [..., null] exact key.
  roomMessagesPrefix: (roomId: string) => ['messages', roomId] as const,

  // User-based messages (direct + anonymous without room)
  userMessages: (userId: string, otherUserId: string, questionId?: string | null) =>
    ['user-messages', userId, otherUserId, questionId ?? null] as const,

  // The caller's thread on someone else's post, fetched by the post alone: the server resolves the
  // other participant, the post's owner (TECH-693). It sits under the user-messages base so every
  // sweep of userMessagesBase (the SSE backfill, a suppressed push) reaches it. The object segment
  // cannot equal a user id or slug, so it never collides with a userMessages() entry.
  postThreadMessages: (userId: string, questionId: string) =>
    ['user-messages', userId, { postOwnerOf: questionId }] as const,
} as const;

/**
 * Cache size limits for performance management
 */
export const CACHE_CONFIG = {
  // Maximum messages to keep in memory per room
  MAX_MESSAGES_PER_ROOM: 1000,

  // Maximum rooms to keep in cache
  MAX_CACHED_ROOMS: 50,

  // Maximum offline messages to queue
  MAX_OFFLINE_QUEUE: 100,

  // LocalStorage key prefixes
  STORAGE_KEYS: {
    MESSAGES: 'meetball_chat_messages_',
    ROOMS: 'meetball_chat_rooms_',
    QUEUE: 'meetball_chat_offline_queue',
    LAST_FETCH: 'meetball_chat_last_fetch_',
    USER_PREFERENCES: 'meetball_chat_preferences',
  },

  // Network retry configuration
  NETWORK: {
    RETRY_ATTEMPTS: 3,
    RETRY_DELAY_MS: 1000,
    MAX_RETRY_DELAY_MS: 30000,
    OFFLINE_CHECK_INTERVAL_MS: 5000,
  },
} as const;

export type CacheKeys = typeof CACHE_KEYS;
export type CacheConfig = typeof CACHE_CONFIG;
